import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiCheckCircle, HiOutlineChatBubbleLeftRight, HiArrowUpRight } from 'react-icons/hi2';
import Swal from 'sweetalert2';
import { saveEnquiry } from '../data/enquiriesData';
import {
  getDivisionCategories,
  getDivisionProducts
} from '../data/divisionProducts';

const benefits = [
  { title: 'In-House Assembly', desc: 'All panels fabricated and wired in our own workshop under strict quality control.' },
  { title: 'Type-Tested Designs', desc: 'Panels are built to IEC/IS standards with type-tested components.' },
  { title: 'Custom Engineering', desc: 'Fully customised single-line diagrams, bus sizing, and protection relay coordination.' },
  { title: 'Fast Lead Times', desc: 'Standard LT panels delivered within 4–6 weeks from order confirmation.' },
  { title: 'Site Commissioning', desc: 'Our engineers handle installation, testing, and protection relay setting at site.' },
  { title: 'Grade-A Licensed', desc: 'All electrical panel works carried out under our Grade-A electrical contractor licence.' },
];

const staticPanelTypes = [
  { name: 'LT Panels', desc: 'Main distribution boards, sub-distribution panels, feeder pillars.' },
  { name: 'HT Panels', desc: 'VCB panels, RMU units, and HT switchgear for medium voltage networks.' },
  { name: 'PCC Panels', desc: 'Power Control Centre panels for centralised incoming power management.' },
  { name: 'MCC Panels', desc: 'Motor Control Centres with DOL, star-delta, VFD and soft-starter modules.' },
  { name: 'APFC Panels', desc: 'Automatic power factor correction panels with capacitor banks.' },
  { name: 'Custom Panel Manufacturing', desc: 'Bespoke AMF, synchronisation and changeover panels built to specification.' },
];

export default function ElectricalPanels() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', city: '', message: '' });

  const loadData = async () => {
    const cats = await getDivisionCategories('panels');
    const prods = await getDivisionProducts('panels');
    setCategories(cats);
    setProducts(prods);
    if (cats.length > 0) {
      setActiveTab(prev => {
        if (prev && cats.some(c => c.name === prev)) return prev;
        return cats[0].name;
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, []);

  const handleChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneDigits = formData.phone.trim();
    if (!/^\d{10}$/.test(phoneDigits)) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Please enter a valid 10-digit number.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      Swal.fire({
        title: 'Invalid Email Address',
        text: 'Please enter a valid email address.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const newEnquiry = {
      id: `enq-${Date.now()}`,
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      city: formData.city,
      message: formData.message || 'Electrical Panel enquiry request.',
      type: 'panels',
      status: 'pending',
      date: new Date().toISOString().replace('T', ' ').slice(0, 16)
    };

    try {
      await saveEnquiry(newEnquiry, true);
      Swal.fire({ title: 'Enquiry Received!', text: `Thank you, ${formData.name}. Our panel engineering team will respond within 24 hours.`, icon: 'success', confirmButtonColor: '#0F5A3A' });
      setFormData({ name: '', phone: '', email: '', city: '', message: '' });
      e.target.reset();
    } catch (err) {
      Swal.fire('Error', 'Failed to submit enquiry. Please try again.', 'error');
    }
  };

  const filtered = products.filter(p => p.category === activeTab);

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/Electrical Panels.png')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Electrical Division</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Electrical <span className="text-gradient-gold">Panels</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Custom-manufactured LT/HT panels, PCC, MCC, APFC, and AMF switchgear — engineered and commissioned by our Grade-A certified team.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-2 text-xs text-body-muted">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span className="text-border-dark">/</span>
          <span>Divisions</span>
          <span className="text-border-dark">/</span>
          <span className="font-semibold text-primary">Electrical Panels</span>
        </div>
      </div>

      {/* Overview */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <span className="eyebrow">Overview</span>
            <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading mb-6">Precision Panel Engineering</h2>
            <div className="space-y-3 text-sm text-body leading-relaxed">
              <p>Green Gusto's Electrical Panel division manufactures custom LT and HT switchgear, PCC, MCC, APFC, and AMF panels from our in-house workshop. Each panel is designed to IS/IEC standards with type-tested components and fully documented single-line diagrams.</p>
              <p>Our Grade-A licensed electrical team handles fabrication, wiring, factory acceptance testing, site installation, and commissioning — providing a complete panel solution from engineering to energisation.</p>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {[
              { v: '300+', l: 'Panels Delivered' },
              { v: 'Grade-A', l: 'Licensed Contractor' },
              { v: 'IEC/IS', l: 'Standards Compliant' },
              { v: '4–6 Wk', l: 'Typical Lead Time' }
            ].map((s, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-5 text-center shadow-card hover:shadow-card-hover hover:-translate-y-1 hover:border-accent/20 transition-all duration-300">
                <span className="text-2xl font-display font-extrabold text-primary block mb-1">{s.v}</span>
                <span className="text-[10px] text-body-muted font-bold uppercase tracking-wider">{s.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Panel Types */}
      <section className="bg-light-section border-y border-border py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <span className="eyebrow">Panel Range</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Types of Panels We Manufacture</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {staticPanelTypes.map((pt, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-6 shadow-card hover:shadow-card-hover hover:-translate-y-1 hover:border-primary/20 transition-all duration-300 group">
                <div className="w-8 h-0.5 bg-accent rounded-full mb-4" />
                <h4 className="text-xs font-bold text-heading uppercase tracking-wide mb-2 group-hover:text-primary transition-colors">{pt.name}</h4>
                <p className="text-xs text-body leading-relaxed">{pt.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Catalog */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <span className="eyebrow">Products</span>
          <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Browse Panel Products</h2>
        </div>
        <div className="flex flex-wrap justify-center items-center gap-3 mb-10">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.name)}
              className={`px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === cat.name
                  ? 'bg-primary text-white shadow-premium'
                  : 'bg-white border border-border text-body hover:border-primary/30 hover:text-primary shadow-card'
                }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filtered.map(prod => (
            <div key={prod.id} className="bg-white border border-border rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover hover:-translate-y-1.5 transition-all duration-300 flex flex-col sm:flex-row group min-h-[260px]">
              <div className="w-full sm:w-52 h-48 sm:h-auto flex-shrink-0 bg-light-section overflow-hidden border-b sm:border-b-0 sm:border-r border-border flex items-center justify-center">
                <img src={prod.image || '/Divisions/electrical panel.jpg'} alt={prod.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6 flex-grow flex flex-col justify-between">
                <div>
                  <span className="badge-primary text-xs mb-2.5 block w-fit">{prod.category}</span>
                  <h3 className="text-base font-bold text-slate-900 mb-1.5 group-hover:text-primary transition-colors">{prod.name}</h3>
                  <p className="text-xs text-body leading-relaxed mb-3">{prod.desc}</p>
                  {prod.specs && Object.keys(prod.specs).length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {Object.entries(prod.specs).map(([k, v]) => (
                        <div key={k} className="bg-light-section rounded-lg px-2.5 py-1.5">
                          <span className="text-[9px] text-body-muted block uppercase tracking-wide truncate">{k}</span>
                          <span className="text-xs font-bold text-primary block truncate mt-0.5">{v}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 mt-4 pt-2">
                  <button
                    onClick={() => document.getElementById('panels-form')?.scrollIntoView({ behavior: 'smooth' })}
                    className="btn-primary w-full justify-center py-2.5 rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all"
                  >
                    Enquire Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits + Form */}
      <section id="panels-form" className="py-24 bg-light-section border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="eyebrow">Why Green Gusto Panels</span>
            <h2 className="text-2xl font-display font-extrabold text-heading mb-8">Manufacturing Advantages</h2>
            <div className="space-y-5">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-4">
                  <HiCheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-heading mb-1">{b.title}</h4>
                    <p className="text-xs text-body leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-charcoal text-white border border-white/5 rounded-3xl p-8 shadow-premium relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-accent/8 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-2">
                <HiOutlineChatBubbleLeftRight className="w-5 h-5 text-accent" />
                <span className="text-xs font-bold text-accent uppercase tracking-widest">Panel Enquiry Form</span>
              </div>
              <h3 className="text-xl font-display font-extrabold text-white mb-2">Request Panel Specifications</h3>
              <p className="text-xs text-white/60 mb-8">Tell us your requirement and our engineers will prepare a detailed proposal.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[['name', 'Full Name', 'text'], ['phone', 'Phone', 'tel'], ['email', 'Email', 'email'], ['city', 'City', 'text']].map(([n, p, t]) => (
                    <div key={n}>
                      <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">{p}</label>
                      <input type={t} name={n} value={formData[n]} onChange={handleChange} required placeholder={p} className="input-dark" />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Message / Requirement</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} rows={3} required placeholder="Describe your panel requirement..." className="input-dark resize-none" />
                </div>
                <button type="submit" className="btn-green rounded-xl w-full justify-center py-3.5 text-sm">
                  Submit Panel Enquiry
                  <HiArrowUpRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
